
import { defineComponent } from 'vue';
import MainLayout from '@/layout/MainLayout.vue';
import SpinnerLoader from '@/components/loader/SpinnerLoader.vue';
import PaginationComponent from '@/components/pagination/PaginationComponent.vue';
import OfferItem from '@/components/offers/OfferItem.vue';
import { useOffersStore } from '@/store/OffersStore';
import { useAuthStore } from '@/store/AuthStore';
import type { OfferStatus } from '@/api/offersService';
import Swal from 'sweetalert2';

export default defineComponent({
  components: {
    MainLayout,
    SpinnerLoader,
    PaginationComponent,
    OfferItem,
  },
  data() {
    const Offers = useOffersStore();
    const Auth = useAuthStore();
    return {
      Offers,
      Auth,
      status: 'pending' as OfferStatus,
      limit: 20,
      offset: 0,
      currentPage: 1,
      isLoading: true,
      isUpdating: false,
    };
  },
  async mounted() {
    if (this.Auth.isLoggedIn) {
      this.Auth.getUserInfo;
      await this.handleOffersPopulation();
    }
    this.isLoading = false;
  },
  methods: {
    async handleOffersPopulation({
      limit,
      offset,
    }: { limit?: number; offset?: number } = {}) {
      const effectiveLimit = limit ?? this.limit;
      const effectiveOffset = offset ?? this.offset;

      const res = await this.Offers.items({
        status: this.status,
        limit: effectiveLimit,
        offset: effectiveOffset,
      });

      if (res && res.success === false) {
        await Swal.fire(
          'Error',
          res.error || 'Failed to fetch offers',
          'error'
        );
      }
    },

    async handleChangeStatusView(status: OfferStatus) {
      this.status = status;
      this.currentPage = 1;
      this.offset = 0;
      this.isLoading = true;
      const res = await this.Offers.items({
        status: this.status,
        limit: this.limit,
        offset: 0,
        reset: true,
      });
      if (res && res.success === false) {
        await Swal.fire(
          'Error',
          res.error || 'Failed to fetch offers',
          'error'
        );
      }
      this.isLoading = false;
    },

    async handleChangeOfferStatus(id: number, status: OfferStatus) {
      this.isUpdating = true;
      const updateRes = await this.Offers.updateOffer({ id, status });
      if (updateRes && updateRes.success === false) {
        await Swal.fire(
          'Error',
          updateRes.error || 'Failed to update offer',
          'error'
        );
      }

      const refreshRes = await this.Offers.items({
        status: this.status,
        limit: this.limit,
        offset: this.offset,
        reset: true,
      });
      if (refreshRes && refreshRes.success === false) {
        await Swal.fire(
          'Error',
          refreshRes.error || 'Failed to fetch offers',
          'error'
        );
      }
      this.isUpdating = false;
    },

    async updatePagination(pageNumber: number) {
      this.isLoading = true;
      this.currentPage = pageNumber;
      this.offset = (pageNumber - 1) * this.limit;
      const res = await this.Offers.items({
        status: this.status,
        limit: this.limit,
        offset: this.offset,
      });
      if (res && res.success === false) {
        await Swal.fire(
          'Error',
          res.error || 'Failed to fetch offers',
          'error'
        );
      }
      this.isLoading = false;
    },
  },
  computed: {
    total(): number {
      return Number(this.Offers.lastAPICall?.total ?? 0);
    },

    emptyOffersMessage(): string {
      switch (this.status) {
        case 'accepted':
          return 'No approved offers.';
        case 'declined':
          return 'No declined offers.';
        case 'pending':
        default:
          return 'No pending offers.';
      }
    },
  },
});
